/**
 * One lead: the caller, what they told the agent, and what happened. The wrap-up card is the
 * agent's call form, where Current Policy Type, Calling For, Pitch Used and Outcome of The Call
 * are required (spec 2.1 FIX: required here and nowhere else, so a vendor can send a name and a
 * number). Convert turns the lead into a policy with its figures carried over (A2).
 */
import {
  CALL_TYPES,
  CALLING_FOR,
  CONVERSATION_OUTCOMES,
  CURRENT_POLICY_STARTS,
  CURRENT_POLICY_TYPES,
  DISQUALIFY_REASONS,
  disqualifyReasonLabel,
  formatPhone,
  leadSourceLabel,
  PITCHES,
  toUsDate,
  type Lead,
} from "@tlcrm/shared";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { ApiError, errorMessage } from "../api/client";
import { useConvertLead, useLead, useLeadOutcome, usePicklists, useUpdateLead } from "../api/hooks";
import { useMe } from "../auth/session";
import { History } from "../components/History";
import { Icon } from "../components/Icon";
import { Timeline } from "../components/Timeline";
import { useToast } from "../components/Toasts";
import { EligibilityFlags } from "../components/kit";
import { ErrorState, FieldBox, LeadStatusMark, Modal, Select } from "../components/ui";
import { useForm } from "../forms/useForm";
import { mayDial } from "../lib/cockpit";
import { dial } from "../lib/dial";
import { age, money, when } from "../lib/format";
import { useNow } from "../lib/useNow";
import { useRecordKey } from "../shell/useShortcuts";
import { LiveCallBar } from "./record/LiveCallBar";

const num = (s: string) => (s.trim() ? Number(s.replace(/[$,]/g, "")) : null);

function Fv({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className="fv">
      <dt>{label}</dt>
      <dd className={empty ? "empty-v" : mono ? "mono" : undefined}>{empty ? "–" : value}</dd>
    </div>
  );
}

export function LeadRecord() {
  const { id = "" } = useParams();
  const lead = useLead(id);
  if (lead.isError)
    return (
      <div className="page">
        <ErrorState error={lead.error} onRetry={() => void lead.refetch()} />
      </div>
    );
  if (lead.isPending) return <div className="record-head" style={{ height: 160 }} />;
  return <Loaded lead={lead.data} />;
}

function Loaded({ lead }: { lead: Lead }) {
  const { can, me } = useMe();
  const now = useNow(30_000);
  const toast = useToast();
  const update = useUpdateLead(lead.id);
  const [tab, setTab] = useState<"details" | "history">("details");
  const [wrapping, setWrapping] = useState(false);
  const [converting, setConverting] = useState(false);
  const [closing, setClosing] = useState(false);
  const open = lead.status !== "converted" && lead.status !== "disqualified";
  const callable = Boolean(lead.phone) && mayDial(lead.state, lead.doNotCall, now);

  useRecordKey({
    d: () => callable && dial(lead.phone!),
    n: () => document.getElementById(`note-${lead.id}`)?.focus(),
    w: () => open && setWrapping(true),
  });

  const reopen = async () => {
    try {
      await update.mutateAsync({ version: lead.version, status: "working" });
      toast("Reopened.");
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };

  return (
    <>
      <LiveCallBar
        contactIds={[lead.contactId]}
        phones={[lead.phone]}
        about={{ leadId: lead.id }}
        facts={
          <>
            <span>
              Lead age <b className="mono">{age(now - Date.parse(lead.receivedAt))}</b>
            </span>
            {lead.callType && <span>{lead.callType}</span>}
            {open && (
              <button type="button" className="btn btn-sm" onClick={() => setWrapping(true)}>
                Call form (W)
              </button>
            )}
          </>
        }
      />
      <div className="record-head">
        <div className="record-crumb">
          <Link to="/leads">Leads</Link>
          <Icon name="chevR" />
          <span>{lead.contactName}</span>
        </div>
        <div className="record-title">
          <span className="face face-lead">
            <Icon name="inbox" />
          </span>
          <div>
            <h1>{lead.contactName}</h1>
            <div className="facts">
              <span className="id-mark id-lead">Lead</span>
              <LeadStatusMark status={lead.status} />
              {lead.callType && <span>{lead.callType}</span>}
              <span>{lead.vendor ?? leadSourceLabel(lead.source)}</span>
              {lead.state && <span>{lead.state}</span>}
              <span className="sep" />
              <span>Owner {lead.owner?.fullName ?? "unassigned"}</span>
              <EligibilityFlags
                state={lead.state}
                language={lead.language}
                consentType={lead.consentType}
                doNotCall={lead.doNotCall}
                licensedStates={me.licensedStates}
                now={now}
              />
            </div>
          </div>
          <div className="actions">
            {lead.phone && (
              <button
                className="btn"
                onClick={() => dial(lead.phone!)}
                disabled={!callable}
                title={callable ? "Dial on the desk (D)" : "Not callable right now: see the flags"}
              >
                <Icon name="phoneOut" /> <span className="mono">{formatPhone(lead.phone)}</span>
              </button>
            )}
            <Link className="btn" to={`/contacts/${lead.contactId}`}>
              <Icon name="user" /> Customer
            </Link>
            {lead.policyId ? (
              <Link className="btn btn-primary" to={`/policies/${lead.policyId}`}>
                <Icon name="briefcase" /> Open policy
              </Link>
            ) : open ? (
              <>
                <button className="btn" onClick={() => setClosing(true)}>
                  Close lost
                </button>
                <button className="btn btn-primary" onClick={() => setConverting(true)}>
                  <Icon name="briefcase" /> Convert to policy
                </button>
              </>
            ) : (
              lead.status === "disqualified" && (
                <button className="btn" onClick={() => void reopen()} disabled={update.isPending}>
                  Reopen
                </button>
              )
            )}
          </div>
        </div>
        <div className="highlights">
          <div>
            <span className="eyebrow">Received</span>
            <div className="v">{when(lead.receivedAt)}</div>
          </div>
          <div>
            <span className="eyebrow">Outcome of the call</span>
            <div className="v">{lead.callOutcome ?? "Not filed"}</div>
          </div>
          <div>
            <span className="eyebrow">Current cover</span>
            <div className="v">{lead.currentPolicyType ?? "–"}</div>
          </div>
          <div>
            <span className="eyebrow">Tries</span>
            <div className="v mono">{lead.attempts}</div>
          </div>
          <div>
            <span className="eyebrow">Consent</span>
            <div className="v">
              {lead.consentType === "none"
                ? "None on file"
                : lead.consentAt
                  ? when(lead.consentAt)
                  : "–"}
            </div>
          </div>
        </div>
        {can("audit:read") && (
          <div className="tabs" role="tablist">
            <button
              className="tab"
              role="tab"
              aria-selected={tab === "details"}
              onClick={() => setTab("details")}
            >
              Details
            </button>
            <button
              className="tab"
              role="tab"
              aria-selected={tab === "history"}
              onClick={() => setTab("history")}
            >
              Field history
            </button>
          </div>
        )}
      </div>
      <div className="record-body main-first">
        <div className="stack">
          {lead.status === "disqualified" && (
            <div className="banner banner-ochre">
              <Icon name="info" />
              Closed lost: {disqualifyReasonLabel(lead.disqualifyReason ?? "other")}.
            </div>
          )}
          {wrapping ? (
            <WrapUp lead={lead} onDone={() => setWrapping(false)} />
          ) : (
            <CallDetails lead={lead} onEdit={open ? () => setWrapping(true) : undefined} />
          )}
          <SourceDetails lead={lead} />
          {tab === "history" && <History entity="lead" id={lead.id} />}
        </div>
        <Timeline kind="leads" id={lead.id} />
      </div>
      {converting && <ConvertModal lead={lead} onClose={() => setConverting(false)} />}
      {closing && <CloseLostModal lead={lead} onClose={() => setClosing(false)} />}
    </>
  );
}

function CallDetails({ lead, onEdit }: { lead: Lead; onEdit?: () => void }) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>The call</h3>
        {onEdit && (
          <button className="btn btn-sm btn-primary" onClick={onEdit}>
            {lead.callOutcome ? "Update wrap-up" : "File wrap-up"}
          </button>
        )}
      </div>
      <div className="card-body">
        <dl className="fields" style={{ margin: 0 }}>
          <Fv label="Call Type" value={lead.callType} />
          <Fv
            label="Date of Conversation"
            value={lead.dateOfConversation && toUsDate(lead.dateOfConversation)}
            mono
          />
          <Fv label="Current Policy Type" value={lead.currentPolicyType} />
          <Fv label="Calling For" value={lead.callingFor.join(", ")} />
          <Fv label="Pitch Used" value={lead.pitchUsed} />
          <Fv label="Outcome of The Call" value={lead.callOutcome} />
          <Fv label="Former Carrier" value={lead.formerCarrier} />
          <Fv label="Current Policy Number" value={lead.currentPolicyNumber} mono />
          <Fv label="Current Policy Start" value={lead.currentPolicyStart} />
          <Fv
            label="Current Coverage Amount"
            value={lead.currentCoverageAmount !== null ? money(lead.currentCoverageAmount) : null}
            mono
          />
          <Fv
            label="Current Monthly Premium"
            value={
              lead.currentMonthlyPremium !== null ? money(lead.currentMonthlyPremium, true) : null
            }
            mono
          />
          <Fv label="Proposed Insured" value={lead.proposedInsuredName} />
          <Fv label="DOB" value={lead.dateOfBirth && toUsDate(lead.dateOfBirth)} mono />
          <Fv label="Notes" value={lead.notes} />
        </dl>
        {lead.description && <p className="lead-description">{lead.description}</p>}
      </div>
    </div>
  );
}

function SourceDetails({ lead }: { lead: Lead }) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Where it came from</h3>
      </div>
      <div className="card-body">
        <dl className="fields" style={{ margin: 0 }}>
          <Fv label="Source" value={leadSourceLabel(lead.source)} />
          <Fv label="Vendor" value={lead.vendor} />
          <Fv label="Vendor's lead id" value={lead.vendorLeadId} mono />
          <Fv label="Campaign" value={lead.campaign} />
          <Fv label="Line" value={lead.line} />
          <Fv label="Cost" value={lead.cost !== null ? money(lead.cost, true) : null} mono />
          <Fv label="Consent given" value={lead.consentAt && when(lead.consentAt)} />
          <Fv label="Consent page" value={lead.consentUrl} />
        </dl>
      </div>
    </div>
  );
}

function WrapUp({ lead, onDone }: { lead: Lead; onDone: () => void }) {
  const toast = useToast();
  const save = useLeadOutcome(lead.id);
  const f = useForm({
    callType: (lead.callType ?? "") as string,
    currentPolicyType: (lead.currentPolicyType ?? "") as string,
    callingFor: lead.callingFor as string[],
    pitchUsed: (lead.pitchUsed ?? "") as string,
    callOutcome: (lead.callOutcome ?? "") as string,
    formerCarrier: lead.formerCarrier ?? "",
    currentPolicyNumber: lead.currentPolicyNumber ?? "",
    currentPolicyStart: (lead.currentPolicyStart ?? "") as string,
    currentCoverageAmount: lead.currentCoverageAmount?.toString() ?? "",
    currentMonthlyPremium: lead.currentMonthlyPremium?.toString() ?? "",
    proposedInsuredName: lead.proposedInsuredName ?? "",
    notes: lead.notes ?? "",
  });
  const v = f.values;
  const toggle = (who: string) =>
    f.set("callingFor")(
      v.callingFor.includes(who) ? v.callingFor.filter((x) => x !== who) : [...v.callingFor, who],
    );

  const submit = async () => {
    try {
      await save.mutateAsync({
        version: lead.version,
        callType: v.callType || null,
        currentPolicyType: v.currentPolicyType || undefined,
        callingFor: v.callingFor,
        pitchUsed: v.pitchUsed || undefined,
        callOutcome: v.callOutcome || undefined,
        formerCarrier: v.formerCarrier,
        currentPolicyNumber: v.currentPolicyNumber,
        currentPolicyStart: v.currentPolicyStart || null,
        currentCoverageAmount: num(v.currentCoverageAmount),
        currentMonthlyPremium: num(v.currentMonthlyPremium),
        proposedInsuredName: v.proposedInsuredName,
        notes: v.notes,
      });
      toast("Wrap-up filed.");
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === "stale_version") f.setFormError(err.message);
      else f.fail(err);
    }
  };

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="card-head">
        <h3>Call wrap-up</h3>
      </div>
      <div className="card-body stack">
        {f.formError && <div className="banner banner-brick">{f.formError}</div>}
        <div className="form-grid">
          <FieldBox
            label="Current Policy Type *"
            htmlFor="wu-cpt"
            error={f.errors.currentPolicyType}
          >
            <Select
              id="wu-cpt"
              value={v.currentPolicyType}
              onChange={f.set("currentPolicyType")}
              options={CURRENT_POLICY_TYPES}
              empty="Choose"
              invalid={Boolean(f.errors.currentPolicyType)}
            />
          </FieldBox>
          <FieldBox label="Pitch Used *" htmlFor="wu-pitch" error={f.errors.pitchUsed}>
            <Select
              id="wu-pitch"
              value={v.pitchUsed}
              onChange={f.set("pitchUsed")}
              options={PITCHES}
              empty="Choose"
              invalid={Boolean(f.errors.pitchUsed)}
            />
          </FieldBox>
          <FieldBox
            label="Outcome of The Call *"
            htmlFor="wu-outcome"
            error={f.errors.callOutcome}
            full
          >
            <Select
              id="wu-outcome"
              value={v.callOutcome}
              onChange={f.set("callOutcome")}
              options={CONVERSATION_OUTCOMES}
              empty="Choose"
              invalid={Boolean(f.errors.callOutcome)}
            />
          </FieldBox>
          <div className="full">
            <span className="field-label">Calling For *</span>
            <div className="checks" role="group" aria-label="Calling For">
              {CALLING_FOR.map(([value, label]) => (
                <label key={value} className="check">
                  <input
                    type="checkbox"
                    checked={v.callingFor.includes(value)}
                    onChange={() => toggle(value)}
                  />
                  {label}
                </label>
              ))}
            </div>
            {f.errors.callingFor && <div className="field-error">{f.errors.callingFor}</div>}
          </div>
          <FieldBox label="Call Type" htmlFor="wu-calltype">
            <Select
              id="wu-calltype"
              value={v.callType}
              onChange={f.set("callType")}
              options={CALL_TYPES}
              empty="None"
            />
          </FieldBox>
          <FieldBox label="Former Carrier" htmlFor="wu-former" error={f.errors.formerCarrier}>
            <input id="wu-former" {...f.text("formerCarrier")} />
          </FieldBox>
          <FieldBox
            label="Current Policy Number"
            htmlFor="wu-cpn"
            error={f.errors.currentPolicyNumber}
          >
            <input id="wu-cpn" {...f.text("currentPolicyNumber", "mono")} />
          </FieldBox>
          <FieldBox label="Current Policy Start" htmlFor="wu-cps">
            <Select
              id="wu-cps"
              value={v.currentPolicyStart}
              onChange={f.set("currentPolicyStart")}
              options={CURRENT_POLICY_STARTS}
              empty="Not asked"
            />
          </FieldBox>
          <FieldBox
            label="Current Coverage Amount"
            htmlFor="wu-cca"
            error={f.errors.currentCoverageAmount}
          >
            <input id="wu-cca" inputMode="decimal" {...f.text("currentCoverageAmount", "mono")} />
          </FieldBox>
          <FieldBox
            label="Current Monthly Premium"
            htmlFor="wu-cmp"
            error={f.errors.currentMonthlyPremium}
          >
            <input id="wu-cmp" inputMode="decimal" {...f.text("currentMonthlyPremium", "mono")} />
          </FieldBox>
          <FieldBox
            label="Proposed Insured"
            htmlFor="wu-pin"
            error={f.errors.proposedInsuredName}
            full
          >
            <input id="wu-pin" placeholder="If not the caller" {...f.text("proposedInsuredName")} />
          </FieldBox>
          <FieldBox label="Notes" htmlFor="wu-notes" error={f.errors.notes} full>
            <textarea id="wu-notes" rows={3} {...f.text("notes")} />
          </FieldBox>
        </div>
      </div>
      <div className="card-foot">
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          style={{ marginLeft: "auto" }}
          disabled={save.isPending}
        >
          {save.isPending ? "Filing…" : "File wrap-up"}
        </button>
      </div>
    </form>
  );
}

function ConvertModal({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const convert = useConvertLead(lead.id);
  const picklists = usePicklists();
  const f = useForm({
    premium: "",
    dateSold: "",
    dateOfBirth: toUsDate(lead.dateOfBirth),
    carrierId: "",
    productId: "",
    totalCoverage: "",
    effectiveDate: "",
    stage: "issued" as "issued" | "closed_won",
  });
  const v = f.values;
  const submit = async () => {
    try {
      const policy = await convert.mutateAsync({
        version: lead.version,
        premium: num(v.premium),
        ...(v.dateSold && { dateSold: v.dateSold }),
        ...(v.dateOfBirth && { dateOfBirth: v.dateOfBirth }),
        carrierId: v.carrierId || null,
        productId: v.productId || null,
        totalCoverage: num(v.totalCoverage),
        effectiveDate: v.effectiveDate,
        stage: v.stage,
      });
      toast(`${policy.name} is sold.`);
      navigate(`/policies/${policy.id}`);
    } catch (err) {
      f.fail(err);
    }
  };
  return (
    <Modal
      title={`Convert ${lead.contactName} to a policy`}
      onClose={onClose}
      width={600}
      footer={
        <>
          {f.formError && (
            <span className="left" style={{ color: "var(--brick)" }}>
              {f.formError}
            </span>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={convert.isPending}
          >
            {convert.isPending ? "Converting…" : "Convert"}
          </button>
        </>
      }
    >
      <div className="stack">
        <p
          className="t2"
          style={{ margin: 0, fontSize: "var(--fs-sm)", lineHeight: "var(--lh-body)" }}
        >
          The lead becomes Qualified. The policy takes its Call Type and Former Carrier, and the
          premium, Date Sold and DOB below, before Expected Commission is worked out.
        </p>
        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <FieldBox label="Lead Premium (annual) *" htmlFor="cv-prem" error={f.errors.premium}>
            <input
              id="cv-prem"
              inputMode="decimal"
              placeholder="1,200.00"
              {...f.text("premium", "mono")}
            />
          </FieldBox>
          <FieldBox label="Date Sold" htmlFor="cv-sold" error={f.errors.dateSold}>
            <input id="cv-sold" placeholder="Today" {...f.text("dateSold", "mono")} />
          </FieldBox>
          <FieldBox label="DOB" htmlFor="cv-dob" error={f.errors.dateOfBirth}>
            <input id="cv-dob" placeholder="MM-DD-YYYY" {...f.text("dateOfBirth", "mono")} />
          </FieldBox>
          <FieldBox label="Stage" htmlFor="cv-stage">
            <Select
              id="cv-stage"
              value={v.stage}
              onChange={(x) => f.set("stage")(x || "issued")}
              options={[
                ["issued", "Issued (waiting on the first draft)"],
                ["closed_won", "Closed Won"],
              ]}
            />
          </FieldBox>
          <FieldBox label="Carrier Sold" htmlFor="cv-carrier">
            <Select
              id="cv-carrier"
              value={v.carrierId}
              onChange={f.set("carrierId")}
              options={(picklists.data?.carriers ?? [])
                .filter((c) => c.active)
                .map((c) => [c.id, c.name] as const)}
              empty="Not chosen"
            />
          </FieldBox>
          <FieldBox label="Policy Type" htmlFor="cv-product">
            <Select
              id="cv-product"
              value={v.productId}
              onChange={f.set("productId")}
              options={(picklists.data?.products ?? [])
                .filter((c) => c.active)
                .map((c) => [c.id, c.name] as const)}
              empty="Not chosen"
            />
          </FieldBox>
          <FieldBox label="Total Coverage" htmlFor="cv-cover" error={f.errors.totalCoverage}>
            <input id="cv-cover" inputMode="decimal" {...f.text("totalCoverage", "mono")} />
          </FieldBox>
          <FieldBox label="Policy Effective Date" htmlFor="cv-eff" error={f.errors.effectiveDate}>
            <input id="cv-eff" placeholder="MM-DD-YYYY" {...f.text("effectiveDate", "mono")} />
          </FieldBox>
          <button type="submit" hidden />
        </form>
      </div>
    </Modal>
  );
}

function CloseLostModal({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const update = useUpdateLead(lead.id);
  const toast = useToast();
  const [reason, setReason] = useState<string>("");
  const save = async () => {
    try {
      await update.mutateAsync({
        version: lead.version,
        status: "disqualified",
        disqualifyReason: reason || "other",
      });
      toast("Closed lost.");
      onClose();
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };
  return (
    <Modal
      title="Close this lead lost"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={update.isPending}
            onClick={() => void save()}
          >
            Close lost
          </button>
        </>
      }
    >
      <FieldBox label="Why" htmlFor="cl-reason">
        <Select
          id="cl-reason"
          value={reason}
          onChange={setReason}
          options={DISQUALIFY_REASONS}
          empty="Other"
        />
      </FieldBox>
    </Modal>
  );
}
